import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { COURSE_CATALOGUE, PATHWAYS_LEARNING } from "@/data/academy";
import { MEDIA } from "@/lib/media";
import { Plus, Minus, ArrowUpRight } from "lucide-react";

export default function Courses() {
  const [open, setOpen] = useState(null);

  return (
    <div data-testid="courses-page">
      <PageHero
        kicker="Courses"
        title="Building Capability That Lasts"
        subtitle="Every SeedKeepers program is designed to develop practical capability that participants can apply immediately. Through field work, ecological understanding and hands-on experience, you'll build the confidence, judgement and technical skills needed to collect native seed, establish seed banks, support restoration projects and continue learning long after the program has finished."
        image={MEDIA.seedPalm}
      />

      {/* Ways to learn */}
      <section className="bg-cream py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Reveal className="mb-12"><p className="overline text-ochre">Ways to learn</p></Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-2">
            {PATHWAYS_LEARNING.map((p, i) => (
              <Reveal key={p.title} delay={(i % 3) * 0.06}>
                <Link to={p.to} className="group block py-6 border-t border-charcoal/12 hover:border-ochre transition-colors">
                  <h3 className="font-serif text-2xl text-rainforest group-hover:text-eucalyptus transition-colors">{p.title}</h3>
                  <p className="text-charcoal/60 font-light mt-2">{p.body}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Catalogue */}
      <section className="bg-sandstone py-24 md:py-36">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12">
          <Reveal className="mb-12 max-w-2xl">
            <p className="overline text-copper mb-6">Course catalogue</p>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-rainforest leading-tight">
              Foundational and specialist courses.
            </h2>
          </Reveal>

          <div>
            {COURSE_CATALOGUE.map((c, i) => {
              const isOpen = open === i;
              return (
                <div key={c.title} className="border-t border-charcoal/15" data-testid={`course-${i}`}>
                  <button onClick={() => setOpen(isOpen ? null : i)} data-testid={`course-toggle-${i}`} className="w-full text-left py-7 flex items-start justify-between gap-6 group">
                    <div>
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <span className="overline text-copper">{c.level}</span>
                        <span className="text-xs text-charcoal/40">· {c.format} · {c.duration}</span>
                      </div>
                      <h3 className="font-serif text-2xl md:text-3xl text-rainforest group-hover:text-eucalyptus transition-colors">{c.title}</h3>
                    </div>
                    <span className="mt-2 text-eucalyptus shrink-0">{isOpen ? <Minus /> : <Plus />}</span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }} className="overflow-hidden">
                        <div className="pb-10 grid grid-cols-1 md:grid-cols-3 gap-8">
                          <p className="md:col-span-2 text-charcoal/75 text-lg font-light leading-relaxed">{c.purpose}</p>
                          <div>
                            <p className="overline text-charcoal/40 mb-2">For</p>
                            <p className="text-charcoal/70 font-light mb-5">{c.audience}</p>
                            <div className="flex flex-wrap gap-2">
                              {c.skills.map((s) => <span key={s} className="text-xs border border-eucalyptus/40 text-eucalyptus px-3 py-1.5">{s}</span>)}
                            </div>
                            <Link to="/contact" className="inline-flex items-center gap-2 mt-6 text-eucalyptus border-b border-ochre/50 pb-0.5 hover:border-ochre transition-colors text-sm">
                              Register interest <ArrowUpRight size={14} />
                            </Link>
                            {c.link && (
                              <a href={c.link.href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 mt-4 text-copper border-b border-copper/40 pb-0.5 hover:border-copper transition-colors text-sm w-fit">
                                {c.link.label} <ArrowUpRight size={14} />
                              </a>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
            <div className="border-t border-charcoal/15" />
          </div>

          <Reveal className="mt-14">
            <Link to="/contact" data-testid="courses-cta" className="inline-block bg-rainforest text-cream px-8 py-4 text-sm tracking-wide hover:bg-eucalyptus transition-colors">
              Register Interest
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
